/**
 * « Tester le circuit » — refonte UX, chantier 5 (maquette 6, bouton « Tester »).
 *
 * Simulation ENTIÈREMENT à l'écran, sur la définition réelle, sans rien écrire en
 * base : on choisit un rôle et un service, on part de l'étape initiale, et à chaque
 * pas on voit les flèches franchissables et la phrase de ce qui se passerait.
 * Le droit de franchir REPRODUIT À L'IDENTIQUE la règle serveur
 * (`droitSurTransition`, workflow.service.ts) : rangs agent 1 < manager 2 < admin 3 ;
 * le service n'est exigé que des non-admins.
 */
import { useState } from 'react';
import { Button } from '@/components/ui';
import type { WorkflowDefinition } from '@/lib/api';
import { phraseFleche } from '@/components/circuits/CircuitEditeurVisuel';

const RANG: Record<string, number> = { agent: 1, manager: 2, admin: 3 };
const SERVICES = ['service_technique', 'grc', 'rh', 'urbanisme', 'comptable', 'scolaire_cantine', 'associations', 'petite_enfance'];
const LIB_SERVICE: Record<string, string> = { service_technique: 'Service technique', grc: 'Accueil (GRC)', rh: 'RH', urbanisme: 'Urbanisme',
  comptable: 'Comptabilité', scolaire_cantine: 'Scolaire', associations: 'Associations', petite_enfance: 'Petite enfance' };

export function CircuitSimulateur({ definition }: { definition: WorkflowDefinition }) {
  const initial = definition.etats.find((e) => e.estInitial) ?? [...definition.etats].sort((a, b) => a.ordre - b.ordre)[0];
  const [role, setRole] = useState<'agent' | 'manager' | 'admin'>('manager');
  const [service, setService] = useState('service_technique');
  const [etatId, setEtatId] = useState(initial?.id ?? '');
  const [journal, setJournal] = useState<string[]>([]);
  if (!initial) return null;
  const nom = (id: string) => definition.etats.find((e) => e.id === id)?.libelle ?? '?';
  const courant = definition.etats.find((e) => e.id === etatId);
  const peut = (t: WorkflowDefinition['transitions'][number]) => {
    const r = RANG[role] ?? 0;
    if (r < (RANG[t.roleMinimum] ?? 99)) return false;
    if (t.serviceRequis && r < 3 && service !== t.serviceRequis) return false;
    return true;
  };
  const sortantes = definition.transitions.filter((t) => t.deEtatId === etatId);

  return (
    <div className="space-y-3 rounded-r-xl border border-warning/30 bg-warning/5 p-4">
      <p className="text-sm font-medium text-ink">Tester le circuit <span className="font-normal text-ink-muted">— simulation à l’écran, rien n’est enregistré</span></p>
      <div className="flex flex-wrap gap-3 text-sm">
        <label className="flex items-center gap-2">Je suis
          <select className="h-9 rounded-r-md border border-border bg-surface px-2" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            <option value="agent">agent</option><option value="manager">manager</option><option value="admin">administrateur</option>
          </select></label>
        <label className="flex items-center gap-2">du service
          <select className="h-9 rounded-r-md border border-border bg-surface px-2" value={service} onChange={(e) => setService(e.target.value)}>
            {SERVICES.map((s) => <option key={s} value={s}>{LIB_SERVICE[s]}</option>)}
          </select></label>
        <Button size="sm" variant="ghost" onClick={() => { setEtatId(initial.id); setJournal([]); }}>Recommencer</Button>
      </div>
      <p className="text-sm text-ink">Le dossier est à l’étape <b>« {courant?.libelle} »</b>{courant?.estFinal ? ' — c’est une fin de parcours.' : '.'}</p>
      {!courant?.estFinal && (
        <div className="flex flex-wrap gap-2">
          {sortantes.length === 0 && <span className="text-xs text-ink-muted">Aucune flèche ne part de cette étape.</span>}
          {sortantes.map((t) => peut(t) ? (
            <Button key={t.id} size="sm" variant="primary" onClick={() => {
              setJournal((j) => [...j, phraseFleche(t, nom(t.deEtatId), nom(t.versEtatId))]); setEtatId(t.versEtatId);
            }}>{t.libelle}</Button>
          ) : (
            <span key={t.id} className="inline-flex h-9 items-center rounded-r-md border border-border px-3 text-xs text-ink-subtle"
              title="Vous n’avez pas le rôle ou le service exigé pour cette flèche">{t.libelle} — non autorisé</span>
          ))}
        </div>
      )}
      {journal.length > 0 && (
        <ol className="list-decimal space-y-1 pl-5 text-xs text-ink">{journal.map((l, i) => <li key={i}>{l}</li>)}</ol>
      )}
    </div>
  );
}
